import React, { useState } from 'react';
import { HardHat, Plus, Users, ClipboardList, AlertTriangle, TrendingUp, CheckCircle2, Clock } from 'lucide-react';

interface TeamMember { initials: string; color: string; }
interface Task { text: string; assignee: string; priority: 'high' | 'medium' | 'low'; }
interface Project {
  id: string;
  name: string;
  status: 'En obra' | 'Terminada' | 'Presupuestando';
  budget: number;
  spent: number;
  progress: number;
  team: TeamMember[];
  pendingTasks: number;
  tasks: Task[];
}

const DEMO_PROJECTS: Project[] = [
  {
    id: '1',
    name: 'Construcción Chalet Paseo del Prado 7, Mad…',
    status: 'Terminada',
    budget: 54000,
    spent: 51200,
    progress: 100,
    team: [
      { initials: 'DV', color: 'bg-orange-500' },
      { initials: 'PS', color: 'bg-purple-500' },
      { initials: 'CR', color: 'bg-red-500' },
    ],
    pendingTasks: 6,
    tasks: [
      { text: 'Revisión final acabados', assignee: 'DV', priority: 'low' },
      { text: 'Documentación final', assignee: 'PS', priority: 'low' },
    ],
  },
  {
    id: '2',
    name: 'Reforma Integral Vivienda Av. de la Paz 18…',
    status: 'Terminada',
    budget: 133000,
    spent: 130900,
    progress: 100,
    team: [
      { initials: 'DV', color: 'bg-orange-500' },
      { initials: 'SF', color: 'bg-blue-500' },
      { initials: 'AL', color: 'bg-green-500' },
    ],
    pendingTasks: 9,
    tasks: [
      { text: 'Certificado final obra', assignee: 'SF', priority: 'medium' },
    ],
  },
  {
    id: '3',
    name: 'Construcción Chalet Av. Constitución 8B, Z…',
    status: 'En obra',
    budget: 130000,
    spent: 70900,
    progress: 54,
    team: [
      { initials: 'DV', color: 'bg-orange-500' },
      { initials: 'MG', color: 'bg-teal-500' },
    ],
    pendingTasks: 4,
    tasks: [
      { text: 'Replanteo cimentación', assignee: 'AL', priority: 'high' },
      { text: 'Encofrado sótano', assignee: 'TM', priority: 'high' },
      { text: 'Pintura interior', assignee: 'CR', priority: 'medium' },
      { text: 'Pavimento gres', assignee: 'RN', priority: 'low' },
    ],
  },
];

const URGENT_TASKS: Task[] = [
  { text: 'Replanteo cimentación', assignee: 'AL', priority: 'high' },
  { text: 'Encofrado sótano', assignee: 'TM', priority: 'high' },
  { text: 'Pintura interior', assignee: 'CR', priority: 'medium' },
  { text: 'Pavimento gres', assignee: 'RN', priority: 'low' },
];

const COST_DATA = [
  { name: 'Construcción Chalet Paseo…', spent: 51200, budget: 54000 },
  { name: 'Reforma Integral Vivienda…', spent: 130900, budget: 133000 },
  { name: 'Construcción Chalet Av. Co…', spent: 70900, budget: 130000 },
];

const priorityDot: Record<string, string> = {
  high: 'bg-red-400',
  medium: 'bg-orange-400',
  low: 'bg-blue-400',
};

const statusBadge: Record<string, string> = {
  'En obra': 'bg-orange-500 text-white',
  Terminada: 'bg-green-600 text-white',
  Presupuestando: 'bg-blue-500 text-white',
};

const fmtK = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(0)}k€` : `${n}€`;

export default function ObrasPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>(DEMO_PROJECTS);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');

  const selectedProject = projects.find(p => p.id === selected);
  const totalBudget = projects.reduce((s, p) => s + p.budget, 0);
  const totalPending = projects.reduce((s, p) => s + p.pendingTasks, 0);

  const addProject = () => {
    if (!newName.trim()) return;
    const np: Project = {
      id: String(Date.now()),
      name: newName.trim(),
      status: 'Presupuestando',
      budget: 0,
      spent: 0,
      progress: 0,
      team: [],
      pendingTasks: 0,
      tasks: [],
    };
    setProjects(prev => [np, ...prev]);
    setSelected(np.id);
    setNewName('');
    setShowNew(false);
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden -m-8">
      {/* Left panel: project list */}
      <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Proyectos</span>
          <button onClick={() => setShowNew(true)} className="p-1 rounded-lg hover:bg-gray-200 text-gray-500">
            <Plus size={16} />
          </button>
        </div>
        {showNew && (
          <div className="px-3 py-2 border-b border-gray-200">
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addProject(); if (e.key === 'Escape') setShowNew(false); }}
              placeholder="Nombre del proyecto…"
              className="w-full text-xs border border-blue-300 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto py-2">
          {projects.map(p => (
            <button
              key={p.id}
              onClick={() => setSelected(p.id === selected ? null : p.id)}
              className={`w-full text-left px-3 py-2.5 transition-colors ${selected === p.id ? 'bg-white border-l-2 border-orange-400' : 'hover:bg-gray-100'}`}
            >
              <div className="text-sm font-medium text-gray-800 truncate">{p.name}</div>
              <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-orange-400 rounded-full" style={{ width: `${p.progress}%` }} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selected ? (
          <>
            {/* Summary KPIs */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Obras en curso</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {projects.filter(p => p.status === 'En obra').length} obras activas ·{' '}
                  {projects.filter(p => p.status === 'Presupuestando').length} presupuestando
                </p>
              </div>
              <div className="flex gap-6 text-right">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{projects.length}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">OBRAS</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{fmtK(totalBudget)}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">IMPORTE TOTAL</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-500">{totalPending}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">TAREAS PEND.</div>
                </div>
              </div>
            </div>

            {/* Project cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {projects.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelected(p.id)}
                  className="text-left bg-white border border-gray-200 rounded-2xl p-4 hover:border-orange-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="font-semibold text-gray-900 text-sm leading-snug pr-2">{p.name}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${statusBadge[p.status]}`}>{p.status.toUpperCase()}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-1">PRESUPUESTO</div>
                  <div className="text-xl font-bold text-gray-900 mb-3">{fmtK(p.budget)}</div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1">
                    <div className={`h-full rounded-full ${p.progress === 100 ? 'bg-green-500' : 'bg-orange-400'}`} style={{ width: `${p.progress}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{p.progress}% completado</span>
                    <span>{100 - p.progress}% restante</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex -space-x-1.5">
                      {p.team.map((m, i) => (
                        <div key={i} className={`w-7 h-7 rounded-full ${m.color} flex items-center justify-center text-white text-xs font-bold border-2 border-white`}>{m.initials}</div>
                      ))}
                    </div>
                    <span className="text-xs text-gray-400">{p.pendingTasks} tareas pend.</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Urgent tasks + cost bars */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Tareas Urgentes</div>
                <div className="space-y-2">
                  {URGENT_TASKS.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${priorityDot[t.priority]}`} />
                      <span className="text-sm text-gray-700 flex-1">{t.text}</span>
                      <span className="text-xs text-gray-400 font-mono">{t.assignee}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Costes por Obra</div>
                <div className="space-y-3">
                  {COST_DATA.map((c, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-700 truncate pr-2">{c.name}</span>
                        <span className="font-semibold text-gray-900 whitespace-nowrap">{fmtK(c.spent)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-400 rounded-full" style={{ width: `${Math.round((c.spent / c.budget) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : selectedProject ? (
          <ProjectDetail project={selectedProject} onBack={() => setSelected(null)} />
        ) : null}
      </div>
    </div>
  );
}

function ProjectDetail({ project, onBack }: { project: Project; onBack: () => void }) {
  return (
    <div>
      <button onClick={onBack} className="text-sm text-blue-600 hover:underline mb-4 flex items-center gap-1">
        ← Volver a obras
      </button>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{project.name}</h2>
          <span className={`mt-1 inline-block text-xs px-2 py-0.5 rounded-full font-semibold ${statusBadge[project.status]}`}>{project.status}</span>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">{(project.budget / 1000).toFixed(0)}k€</div>
          <div className="text-xs text-gray-500">Presupuesto total</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><TrendingUp size={16} className="text-orange-400" /><span className="text-xs text-gray-500">Progreso</span></div>
          <div className="text-2xl font-bold text-gray-900">{project.progress}%</div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${project.progress === 100 ? 'bg-green-500' : 'bg-orange-400'}`} style={{ width: `${project.progress}%` }} />
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle size={16} className="text-red-400" /><span className="text-xs text-gray-500">Tareas pendientes</span></div>
          <div className="text-2xl font-bold text-gray-900">{project.pendingTasks}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-blue-400" /><span className="text-xs text-gray-500">Equipo</span></div>
          <div className="flex -space-x-1.5 mt-2">
            {project.team.map((m, i) => (
              <div key={i} className={`w-8 h-8 rounded-full ${m.color} flex items-center justify-center text-white text-xs font-bold border-2 border-white`}>{m.initials}</div>
            ))}
            {project.team.length === 0 && <span className="text-sm text-gray-400">Sin equipo</span>}
          </div>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <ClipboardList size={16} />Tareas
        </div>
        {project.tasks.length === 0 ? (
          <p className="text-sm text-gray-400">No hay tareas para esta obra.</p>
        ) : (
          <div className="space-y-2">
            {project.tasks.map((t, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                <div className={`w-2.5 h-2.5 rounded-full ${priorityDot[t.priority]}`} />
                <span className="flex-1 text-sm text-gray-700">{t.text}</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-mono">{t.assignee}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
